'use client';

import { useCallback, useState } from 'react';
import { ArrowsClockwiseIcon, ArrowDownRightIcon, ArrowUpRightIcon, BellIcon } from '@phosphor-icons/react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { usePrivacy } from '@/contexts/PrivacyContext';
import { useBrowserNotifications } from '@/hooks/useBrowserNotifications';
import { useNotificationInbox } from '@/hooks/queries/useNotificationInbox';
import { useMySpaceInvites } from '@/hooks/queries/useSpaceInvitations';
import { SpaceInvitations } from '@/components/spaces/SpaceInvitations';
import { inAppPath } from '@/lib/courier-inbox';
import { relativeTimeLabel } from '@/lib/relative-time';
import { useRouter } from '@/lib/router';
import { track } from '@/lib/telemetry';
import type { InboxNotification } from '@/types/notifications';

const HIDDEN_AMOUNT = 'R$ •••••';
/**
 * The header bell: one entry per sync digest (never per transaction), unread accent bar,
 * top-3 sample with income/expense marks. Content comes theme-blind from the pipeline —
 * direction maps onto the theme-aware amount tokens HERE, so the same notification is
 * correct in light and dark. Renders nothing while the environment has notifications off.
 */
export function NotificationBell() {
  const {
    enabled,
    notifications,
    unreadCount,
    isLoading,
    markRead,
    markAllAsRead,
  } = useNotificationInbox();
  const invitations = useMySpaceInvites();
  const pendingInvites = invitations.error ? 0 : invitations.data?.length ?? 0;
  const badgeCount = unreadCount + pendingInvites;
  const [open, setOpen] = useState(false);
  const handleInviteAccepted = () => setOpen(false);
  const router = useRouter();

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) track('ui.notifications.bell_opened', { unread: unreadCount });
    },
    [unreadCount],
  );

  const handleView = useCallback(
    async (notification: InboxNotification) => {
      track('action.notifications.entry_clicked', { message_id: notification.messageId });
      if (!notification.read) {
        // Best-effort: a failed mark-read must neither block navigation nor surface as
        // an unhandled rejection — the next poll reconciles the read state anyway.
        await markRead(notification.messageId).catch(() => undefined);
      }
      setOpen(false);
      // Each message navigates through its OWN rendered action; only same-app links are
      // honored (in-app routing), anything else falls back to the transactions page.
      router.push(inAppPath(notification.actionHref) ?? '/transactions');
    },
    [markRead, router],
  );

  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAllAsRead();
    } catch {
      // Non-fatal: the badge simply stays until the next poll or retry.
    }
  }, [markAllAsRead]);

  useBrowserNotifications({
    enabled,
    notifications,
    isLoading,
    onOpen: handleView,
  });

  if (!enabled && !invitations.enabled) return null;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notification-bell"
          className="relative cursor-pointer text-text-secondary transition-colors hover:text-brand-700"
          aria-label={notificationBellLabel(unreadCount, pendingInvites)}
        >
          <BellIcon size={18} />
          {badgeCount > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -top-1.5 -right-1.5 flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground tabular-nums"
            >
              {badgeCount > 9 ? '9+' : badgeCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={10} className="w-88 max-w-[calc(100vw-2rem)] p-0">
        <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
          <p className="text-sm font-semibold text-text-primary">Notificações</p>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={() => void handleMarkAllRead()}
              className="cursor-pointer text-xs font-medium text-primary transition-colors hover:text-brand-800"
            >
              Marcar todas como lidas
            </button>
          )}
        </div>
        <SpaceInvitations surface="bell" onAccepted={handleInviteAccepted} />
        {notifications.length === 0 && !pendingInvites && !invitations.isLoading && !invitations.error ? (
          <p className="px-4 py-8 text-center text-sm text-text-tertiary">
            Nenhuma notificação por aqui.
          </p>
        ) : (
          <ul className="max-h-96 overflow-y-auto" role="list">
            {notifications.map((notification) => (
              <NotificationEntry
                key={notification.messageId}
                notification={notification}
                onView={handleView}
              />
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

function notificationBellLabel(unreadCount: number, pendingInvites: number): string {
  const parts = ['Notificações'];
  if (unreadCount > 0) parts.push(`${unreadCount} ${unreadCount === 1 ? 'não lida' : 'não lidas'}`);
  if (pendingInvites > 0) parts.push(`${pendingInvites} ${pendingInvites === 1 ? 'convite pendente' : 'convites pendentes'}`);
  return parts.join(', ');
}

function NotificationEntry({
  notification,
  onView,
}: {
  notification: InboxNotification;
  onView: (notification: InboxNotification) => Promise<void>;
}) {
  const { hideValues } = usePrivacy();
  const digest = notification.digest;

  return (
    <li className="relative border-b border-border-subtle last:border-b-0">
      {!notification.read && (
        <span className="absolute inset-y-3 left-0 w-0.5 rounded-full bg-primary" aria-hidden="true" />
      )}
      <button
        type="button"
        onClick={() => void onView(notification)}
        className="block w-full cursor-pointer px-4 py-3 text-left transition-colors hover:bg-surface-sunken"
      >
        <div className="flex items-start gap-3">
          <ArrowsClockwiseIcon
            size={16}
            className={`mt-0.5 shrink-0 ${notification.read ? 'text-text-tertiary' : 'text-primary'}`}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-3">
              <p className={`min-w-0 text-sm ${notification.read ? 'font-medium text-text-secondary' : 'font-semibold text-text-primary'}`}>
                {notification.title}
              </p>
              <NotificationTime createdAt={notification.createdAt} />
            </div>
            {(digest && digest.institutions.length > 0) ? (
              <p className="mt-0.5 text-sm text-text-secondary">
                {digest.institutions.map((i) => `${i.name} (${i.count})`).join(' · ')}
              </p>
            ) : notification.preview ? (
              <p className="mt-0.5 text-sm text-text-secondary">{notification.preview}</p>
            ) : null}
            {digest && digest.sample.length > 0 && (
              <div className="mt-2 rounded-lg bg-surface-sunken px-3 py-1.5">
                {digest.sample.map((entry, index) => (
                  <div
                    // Index key: two identical purchases (same merchant, same amount) can
                    // legally coexist in the sample, and the list is static per message.
                    key={index}
                    className="flex items-baseline justify-between gap-3 py-0.5 text-sm"
                  >
                    <span className="flex min-w-0 items-baseline gap-1.5 text-text-secondary">
                      {entry.direction === 'income' ? (
                        <ArrowUpRightIcon size={12} className="shrink-0 text-success" weight="bold" />
                      ) : (
                        <ArrowDownRightIcon size={12} className="shrink-0 text-destructive" weight="bold" />
                      )}
                      <span className="truncate">{entry.name}</span>
                    </span>
                    <span
                      className={`shrink-0 font-mono tabular-nums ${entry.direction === 'income' ? 'text-success' : 'text-destructive'}`}
                    >
                      {hideValues ? HIDDEN_AMOUNT : entry.amount}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-1.5 text-xs font-medium text-primary">
              {notification.actionLabel ?? 'Ver transações'}
            </p>
          </div>
        </div>
      </button>
    </li>
  );
}

function NotificationTime({ createdAt }: { createdAt: string | null }) {
  // No timer of its own: the popover mounts its content on each open, so the label is
  // computed fresh per open. It can lag only while the popover is held open.
  const label = createdAt ? relativeTimeLabel(createdAt) : null;
  if (!label) return null;
  return <span className="shrink-0 text-xs text-text-tertiary">{label}</span>;
}
